/**
 * Type-safe localStorage utilities for Profit-Analysis app
 * Centralizes all localStorage keys and provides type-safe getters/setters
 */

import { JobType } from '../contexts/JobTypeContext';

// Define all possible localStorage keys
export type StorageKey =
  | 'pa.selectedPAType'
  | 'pa.soldListFilters'
  | 'pa.specListFilters'
  | 'pa.soldListPagination'
  | 'pa.specListPagination'
  | 'pa.soldDateRange'
  | 'pa.specDateRange'
  | 'pa.userPreferences';

// Define filter structure based on History page
export interface ListFilters {
  quickFilterValues: string[];
  items: FilterItem[];
  stage: string;
}

export interface FilterItem {
  id?: string | number;
  field: string;
  operator: string;
  value: string | string[];
}

export interface PaginationModel {
  page: number;
  pageSize: number;
}

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface UserPreferences {
  theme?: 'light' | 'dark';
  defaultJobType?: JobType;
  // Add more preferences as needed
}

// utils/localStorage.ts — add this alongside your existing helpers

export const initializeLocalStorage = (): void => {
  const defaults: Array<[StorageKey, unknown]> = [
    ['pa.selectedPAType',      'sold'],
    ['pa.soldListFilters',     { quickFilterValues: [''], items: [], stage: 'All' }],
    ['pa.specListFilters',     { quickFilterValues: [''], items: [], stage: 'All' }],
    ['pa.soldListPagination',  { page: 0, pageSize: 50 }],
    ['pa.specListPagination',  { page: 0, pageSize: 50 }],
    ['pa.soldDateRange',       { startDate: null, endDate: null }],
    ['pa.specDateRange',       { startDate: null, endDate: null }],
    ['pa.userPreferences',     {}],
  ];

  defaults.forEach(([key, value]) => {
    if (localStorage.getItem(key) === null) {
      setLocalStorage(key, value);
    }
  });
};


/**
 * Generic localStorage getter with type safety and error handling
 */
export const getLocalStorage = <T = any>(key: StorageKey, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Generic localStorage setter with error handling
 */
export const setLocalStorage = <T = any>(key: StorageKey, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error writing localStorage key "${key}":`, error);
  }
};

/**
 * Remove item from localStorage
 */
export const removeLocalStorage = (key: StorageKey): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
};

/**
 * Clear all app-related localStorage items
 */
export const clearAllLocalStorage = (): void => {
  const keys: StorageKey[] = [
    'pa.selectedPAType',
    'pa.soldListFilters',
    'pa.specListFilters',
    'pa.soldListPagination',
    'pa.specListPagination',
    'pa.soldDateRange',
    'pa.specDateRange',
    'pa.userPreferences',
  ];

  keys.forEach((key) => removeLocalStorage(key));
};

// ============================================================================
// Specific Helpers for Common Operations
// ============================================================================

/**
 * Get filters for Sold job type
 */
export const getSoldFilters = (): ListFilters => {
  return getLocalStorage<ListFilters>('pa.soldListFilters', {
    quickFilterValues: [''],
    items: [],
    stage: 'All',
  });
};

/**
 * Set filters for Sold job type
 */
export const setSoldFilters = (filters: ListFilters): void => {
  setLocalStorage('pa.soldListFilters', filters);
};

/**
 * Get filters for Spec job type
 */
export const getSpecFilters = (): ListFilters => {
  return getLocalStorage<ListFilters>('pa.specListFilters', {
    quickFilterValues: [''],
    items: [],
    stage: 'All',
  });
};

/**
 * Set filters for Spec job type
 */
export const setSpecFilters = (filters: ListFilters): void => {
  setLocalStorage('pa.specListFilters', filters);
};

/**
 * Get pagination for Sold job type
 */
export const getSoldPagination = (): PaginationModel => {
  return getLocalStorage<PaginationModel>('pa.soldListPagination', {
    page: 0,
    pageSize: 50,
  });
};

/**
 * Set pagination for Sold job type
 */
export const setSoldPagination = (pagination: PaginationModel): void => {
  setLocalStorage('pa.soldListPagination', pagination);
};

/**
 * Get pagination for Spec job type
 */
export const getSpecPagination = (): PaginationModel => {
  return getLocalStorage<PaginationModel>('pa.specListPagination', {
    page: 0,
    pageSize: 50,
  });
};

/**
 * Set pagination for Spec job type
 */
export const setSpecPagination = (pagination: PaginationModel): void => {
  setLocalStorage('pa.specListPagination', pagination);
};

/**
 * Get user preferences
 */
export const getUserPreferences = (): UserPreferences => {
  return getLocalStorage<UserPreferences>('pa.userPreferences', {});
};

/**
 * Set user preferences
 */
export const setUserPreferences = (preferences: UserPreferences): void => {
  setLocalStorage('pa.userPreferences', preferences);
};

/**
 * Get selected PA job type
 */
export const getSelectedPAType = (): JobType => {
  const stored = localStorage.getItem('pa.selectedPAType');
  return (stored === 'sold' || stored === 'spec') ? stored : 'sold';
};

/**
 * Set selected PA job type
 */
export const setSelectedPAType = (jobType: JobType): void => {
  localStorage.setItem('pa.selectedPAType', jobType);
};